import { clampMoney, roundMoney } from '../money';

/** Kind of per-line increment/discount. */
export type DiscountType = 'PERCENTAGE' | 'AMOUNT' | 'NONE';

export type LineAdjustmentType = DiscountType;

export interface LinePricingFields {
  markupType: LineAdjustmentType;
  markupValue: number;
  adjustedPrice: number;
  discountType: LineAdjustmentType;
  discountValue: number;
  finalPrice: number;
}

export interface LinePricingDistributionLine {
  id: number;
  originalPrice: number;
  markupType?: LineAdjustmentType;
  markupValue?: number;
}

export function calculateAdjustedPrice(
  originalPrice: number,
  markupType: LineAdjustmentType = 'NONE',
  markupValue = 0,
): number {
  const price = roundMoney(originalPrice);

  if (markupType === 'NONE' || !markupValue) {
    return price;
  }

  if (markupType === 'PERCENTAGE') {
    return roundMoney(price * (1 + Math.max(0, Number(markupValue)) / 100));
  }

  if (markupType === 'AMOUNT') {
    return roundMoney(price + Math.max(0, Number(markupValue)));
  }

  return price;
}

export function calculateLineMarkupAmount(
  originalPrice: number,
  markupType: LineAdjustmentType = 'NONE',
  markupValue = 0,
): number {
  const adjustedPrice = calculateAdjustedPrice(originalPrice, markupType, markupValue);
  return roundMoney(adjustedPrice - roundMoney(originalPrice));
}

export function calculateLineDiscountAmountOnAdjusted(
  adjustedPrice: number,
  discountType: LineAdjustmentType = 'NONE',
  discountValue = 0,
): number {
  const price = roundMoney(adjustedPrice);

  if (discountType === 'NONE' || !discountValue) {
    return 0;
  }

  if (discountType === 'PERCENTAGE') {
    const safePercentage = Math.min(Number(discountValue), 99);
    return roundMoney((price * safePercentage) / 100);
  }

  if (discountType === 'AMOUNT') {
    return roundMoney(Math.min(Number(discountValue), price));
  }

  return 0;
}

export function calculateLineFinalPrice(
  originalPrice: number,
  markupType: LineAdjustmentType = 'NONE',
  markupValue = 0,
  discountType: LineAdjustmentType = 'NONE',
  discountValue = 0,
): number {
  const adjustedPrice = calculateAdjustedPrice(originalPrice, markupType, markupValue);
  const discountAmount = calculateLineDiscountAmountOnAdjusted(
    adjustedPrice,
    discountType,
    discountValue,
  );
  return clampMoney(adjustedPrice - discountAmount);
}

export function buildLinePricingFields(
  originalPrice: number,
  markupType: LineAdjustmentType = 'NONE',
  markupValue = 0,
  discountType: LineAdjustmentType = 'NONE',
  discountValue = 0,
): LinePricingFields {
  const normalizedMarkupValue = markupType === 'NONE' ? 0 : roundMoney(Math.max(0, markupValue));
  const normalizedDiscountValue =
    discountType === 'NONE' ? 0 : roundMoney(Math.max(0, discountValue));
  const adjustedPrice = calculateAdjustedPrice(originalPrice, markupType, normalizedMarkupValue);

  return {
    markupType,
    markupValue: normalizedMarkupValue,
    adjustedPrice,
    discountType,
    discountValue: normalizedDiscountValue,
    finalPrice: calculateLineFinalPrice(
      originalPrice,
      markupType,
      normalizedMarkupValue,
      discountType,
      normalizedDiscountValue,
    ),
  };
}

export function buildDefaultLinePricingFields(originalPrice: number): LinePricingFields {
  return buildLinePricingFields(originalPrice, 'NONE', 0, 'NONE', 0);
}

export function isLineDiscountInvalid(
  adjustedPrice: number,
  discountType: LineAdjustmentType,
  discountValue: number,
): boolean {
  if (discountType === 'NONE' || !discountValue) {
    return false;
  }
  if (discountType === 'PERCENTAGE') {
    return discountValue > 99;
  }
  if (discountType === 'AMOUNT') {
    // Allow full discount to 0; only reject amounts above the adjusted price.
    return discountValue > roundMoney(adjustedPrice);
  }
  return false;
}

export function hasLineMarkup(markupType: LineAdjustmentType, markupValue: number): boolean {
  return markupType !== 'NONE' && markupValue > 0;
}

export function hasLineDiscount(discountType: LineAdjustmentType, discountValue: number): boolean {
  return discountType !== 'NONE' && discountValue > 0;
}

export function sumCatalogPrices(products: Array<{ originalPrice: number }>): number {
  return roundMoney(
    products.reduce((total, product) => total + roundMoney(product.originalPrice), 0),
  );
}

/**
 * Pieza sin precio cargado (base Bs 0). Su precio se fija con un incremento por pieza en Bs; un %
 * sobre 0 no tiene efecto. Si hay alguna, los ajustes al total no se usan (se fuerza "por pieza").
 * `basePrice`: catálogo al crear, precio ya aceptado en edición (`linePricingDisplayBase`).
 */
export function isZeroPriceLine(basePrice: number): boolean {
  return roundMoney(basePrice) <= 0;
}

/**
 * True when some piece has no price (Bs 0). Order-level adjustments are distributed by price, so
 * they are not used then: increment and discount are loaded per piece.
 */
export function hasZeroPriceLine(products: Array<{ originalPrice: number }>): boolean {
  return products.some((product) => isZeroPriceLine(product.originalPrice));
}

/** True when the products have a positive total to distribute an order-level amount against. */
export function hasPositiveCatalogSubtotal(products: Array<{ originalPrice: number }>): boolean {
  return sumCatalogPrices(products) > 0;
}

/**
 * Prefer stored adjustedPrice − originalPrice so summary matches line totals.
 * Falls back to recomputing from markup fields when adjustedPrice is missing.
 */
export function sumLineMarkupAmounts(
  products: Array<{
    originalPrice: number;
    markupType: LineAdjustmentType;
    markupValue: number;
    adjustedPrice?: number;
  }>,
): number {
  return roundMoney(
    products.reduce((total, product) => {
      const original = roundMoney(product.originalPrice);
      const adjusted =
        product.adjustedPrice != null && Number.isFinite(Number(product.adjustedPrice))
          ? roundMoney(product.adjustedPrice)
          : calculateAdjustedPrice(product.originalPrice, product.markupType, product.markupValue);
      return total + roundMoney(Math.max(0, adjusted - original));
    }, 0),
  );
}

/**
 * Prefer stored adjusted − final so order ledger always closes.
 * Falls back to declared discount fields when finalPrice is missing.
 */
export function sumLineDiscountAmounts(
  products: Array<{
    adjustedPrice: number;
    discountType: LineAdjustmentType;
    discountValue: number;
    finalPrice?: number;
  }>,
): number {
  return roundMoney(
    products.reduce((total, product) => {
      if (product.finalPrice != null && Number.isFinite(Number(product.finalPrice))) {
        const adjusted = roundMoney(product.adjustedPrice);
        const finalPrice = roundMoney(product.finalPrice);
        return total + roundMoney(Math.max(0, adjusted - finalPrice));
      }
      return (
        total +
        calculateLineDiscountAmountOnAdjusted(
          product.adjustedPrice,
          product.discountType,
          product.discountValue,
        )
      );
    }, 0),
  );
}

export function sumLineFinalPrices(products: Array<{ finalPrice: number }>): number {
  return roundMoney(products.reduce((total, product) => total + roundMoney(product.finalPrice), 0));
}

/**
 * Distributes a target total by weight.
 * Zero-weight lines always get 0 and never absorb rounding remainder.
 * When every weight is 0, every line stays 0 (no invented 0.01).
 */
const distributeFinalTotalByWeight = (
  items: Array<{ id: number; weight: number }>,
  targetFinalTotal: number,
): Map<number, number> => {
  const result = new Map<number, number>();
  if (items.length === 0) {
    return result;
  }

  const normalized = items.map((item) => ({
    id: item.id,
    weight: roundMoney(item.weight),
  }));

  const positive = normalized.filter((item) => item.weight > 0);
  normalized
    .filter((item) => item.weight <= 0)
    .forEach((item) => {
      result.set(item.id, 0);
    });

  if (positive.length === 0) {
    return result;
  }

  const weightTotal = roundMoney(positive.reduce((sum, item) => sum + item.weight, 0));
  const safeTarget = roundMoney(Math.max(0, targetFinalTotal));
  let allocated = 0;

  positive.forEach((item, index) => {
    if (index === positive.length - 1) {
      result.set(item.id, roundMoney(Math.max(0, safeTarget - allocated)));
      return;
    }
    const share = roundMoney((safeTarget * item.weight) / weightTotal);
    result.set(item.id, share);
    allocated = roundMoney(allocated + share);
  });

  return result;
};

/**
 * Order-level Bs discount is taken from the order total and distributed by price, so the cap is
 * the products total (full discount to 0 is allowed).
 */
export function getMaxUniformDiscountAmount(products: Array<{ adjustedPrice: number }>): number {
  if (products.length === 0) {
    return 0;
  }
  const adjustedSubtotal = roundMoney(
    products.reduce((total, product) => total + roundMoney(product.adjustedPrice), 0),
  );
  return Math.max(0, adjustedSubtotal);
}

/**
 * Extra discount in edit cannot take the order below what is already paid.
 * Remaining = accepted/adjusted products + charges − amount paid.
 */
export function getMaxExtraDiscountAmount(params: {
  adjustedPrices: number[];
  chargesTotal: number;
  amountPaid: number;
}): number {
  const productsTotal = roundMoney(
    params.adjustedPrices.reduce((total, price) => total + roundMoney(price), 0),
  );
  const ceiling = roundMoney(productsTotal + roundMoney(params.chargesTotal));
  const floor = roundMoney(params.amountPaid);
  return Math.max(0, roundMoney(ceiling - floor));
}

export function doesEditTotalFallBelowPaid(
  productsTotal: number,
  chargesTotal: number,
  amountPaid: number,
): boolean {
  return (
    roundMoney(roundMoney(productsTotal) + roundMoney(chargesTotal)) + 0.01 < roundMoney(amountPaid)
  );
}

/** Catalog in create; accepted line total while an edit layer is active. */
export function linePricingDisplayBase(product: {
  originalPrice: number;
  editLayerBasePrice?: number | null;
}): number {
  if (product.editLayerBasePrice != null && Number.isFinite(Number(product.editLayerBasePrice))) {
    return roundMoney(product.editLayerBasePrice);
  }
  return roundMoney(product.originalPrice);
}

export function clampUniformDiscountValue(
  discountType: 'PERCENTAGE' | 'AMOUNT',
  value: number,
  products: Array<{ adjustedPrice: number }>,
): number {
  const safeValue = roundMoney(Math.max(0, value));
  if (discountType === 'PERCENTAGE') {
    return Math.min(safeValue, 99);
  }
  return Math.min(safeValue, getMaxUniformDiscountAmount(products));
}

export function isUniformAmountDiscountInvalid(
  products: Array<{ adjustedPrice: number }>,
  orderDiscountAmount: number,
): boolean {
  if (products.length === 0 || !orderDiscountAmount) {
    return false;
  }
  return roundMoney(orderDiscountAmount) > getMaxUniformDiscountAmount(products);
}

export function applyUniformDiscountToLines(
  lines: LinePricingDistributionLine[],
  settings: { discountType: 'PERCENTAGE' | 'AMOUNT'; discountValue: number },
): Map<number, Pick<LinePricingFields, 'discountType' | 'discountValue' | 'finalPrice'>> {
  const result = new Map<
    number,
    Pick<LinePricingFields, 'discountType' | 'discountValue' | 'finalPrice'>
  >();

  if (lines.length === 0) {
    return result;
  }

  const enrichedLines = lines.map((line) => {
    const markupType = line.markupType ?? 'NONE';
    const markupValue = line.markupValue ?? 0;
    const adjustedPrice = calculateAdjustedPrice(line.originalPrice, markupType, markupValue);
    return {
      ...line,
      markupType,
      markupValue,
      adjustedPrice,
    };
  });

  // Bs: the amount comes off the order total, distributed by each piece's (adjusted) price.
  if (settings.discountType === 'AMOUNT') {
    const adjustedSubtotal = roundMoney(
      enrichedLines.reduce((total, line) => total + line.adjustedPrice, 0),
    );
    const targetFinalTotal = roundMoney(adjustedSubtotal - settings.discountValue);
    const finalPrices = distributeFinalTotalByWeight(
      enrichedLines.map((line) => ({ id: line.id, weight: line.adjustedPrice })),
      targetFinalTotal,
    );

    enrichedLines.forEach((line) => {
      const finalPrice = finalPrices.get(line.id) ?? line.adjustedPrice;
      const discountValue = roundMoney(line.adjustedPrice - finalPrice);
      result.set(line.id, {
        discountType: 'AMOUNT',
        discountValue,
        finalPrice,
      });
    });

    return result;
  }

  enrichedLines.forEach((line) => {
    const discountValue = roundMoney(Math.max(0, settings.discountValue));
    result.set(line.id, {
      discountType: 'PERCENTAGE',
      discountValue,
      finalPrice: calculateLineFinalPrice(
        line.originalPrice,
        line.markupType,
        line.markupValue,
        'PERCENTAGE',
        discountValue,
      ),
    });
  });

  return result;
}

export function applyUniformMarkupToLines(
  lines: LinePricingDistributionLine[],
  settings: { markupType: 'PERCENTAGE' | 'AMOUNT'; markupValue: number },
): Map<
  number,
  Pick<LinePricingFields, 'markupType' | 'markupValue' | 'adjustedPrice' | 'finalPrice'>
> {
  const result = new Map<
    number,
    Pick<LinePricingFields, 'markupType' | 'markupValue' | 'adjustedPrice' | 'finalPrice'>
  >();

  if (lines.length === 0) {
    return result;
  }

  const leaveUnchanged = () => {
    lines.forEach((line) => {
      const originalPrice = roundMoney(line.originalPrice);
      result.set(line.id, {
        markupType: 'NONE',
        markupValue: 0,
        adjustedPrice: originalPrice,
        finalPrice: originalPrice,
      });
    });
    return result;
  };

  // Bs: the amount is added to the order total, distributed by each piece's catalog price.
  if (settings.markupType === 'AMOUNT') {
    const originalSubtotal = sumCatalogPrices(lines);
    const markupValue = roundMoney(Math.max(0, settings.markupValue));

    // No positive catalog base → order-level amount markup does not apply (use per-piece).
    if (originalSubtotal <= 0 || markupValue <= 0) {
      return leaveUnchanged();
    }

    const targetAdjustedTotal = roundMoney(originalSubtotal + markupValue);
    // Zero-price lines are excluded from proportional share via weight 0.
    const adjustedPrices = distributeFinalTotalByWeight(
      lines.map((line) => ({ id: line.id, weight: line.originalPrice })),
      targetAdjustedTotal,
    );

    lines.forEach((line) => {
      const originalPrice = roundMoney(line.originalPrice);
      const adjustedPrice = adjustedPrices.get(line.id) ?? originalPrice;
      const lineMarkupValue = roundMoney(adjustedPrice - originalPrice);

      if (originalPrice <= 0) {
        result.set(line.id, {
          markupType: 'NONE',
          markupValue: 0,
          adjustedPrice: originalPrice,
          finalPrice: originalPrice,
        });
        return;
      }

      result.set(line.id, {
        markupType: 'AMOUNT',
        markupValue: lineMarkupValue,
        adjustedPrice,
        finalPrice: adjustedPrice,
      });
    });

    return result;
  }

  // Percentage: 0 × % stays 0; positive lines scale as usual.
  lines.forEach((line) => {
    const fields = buildLinePricingFields(
      line.originalPrice,
      settings.markupType,
      settings.markupValue,
      'NONE',
      0,
    );
    result.set(line.id, {
      markupType: fields.markupType,
      markupValue: fields.markupValue,
      adjustedPrice: fields.adjustedPrice,
      finalPrice: fields.finalPrice,
    });
  });

  return result;
}

export function normalizeAdjustmentType(value: unknown): 'PERCENTAGE' | 'AMOUNT' {
  return value === 'PERCENTAGE' ? 'PERCENTAGE' : 'AMOUNT';
}

export interface CommercialPricingDraft {
  markupEnabled: boolean;
  allowIndividualMarkups: boolean;
  markupType: unknown;
  markupValue: number;
  discountEnabled: boolean;
  allowIndividualDiscounts: boolean;
  discountType: unknown;
  discountValue: number;
}

type PerPieceDraftFlags = Pick<
  CommercialPricingDraft,
  'markupEnabled' | 'allowIndividualMarkups' | 'discountEnabled' | 'allowIndividualDiscounts'
>;

/**
 * When some piece has no price (Bs 0) order-level adjustments are not used, so any enabled
 * increment/discount switches to per piece. Returns the same object when nothing changes, so it
 * can be passed straight to a state setter without causing a re-render.
 */
export function toPerPieceDraft<T extends PerPieceDraftFlags>(draft: T): T {
  const hasUniformMarkup = draft.markupEnabled && !draft.allowIndividualMarkups;
  const hasUniformDiscount = draft.discountEnabled && !draft.allowIndividualDiscounts;
  if (!hasUniformMarkup && !hasUniformDiscount) {
    return draft;
  }
  return {
    ...draft,
    allowIndividualMarkups: draft.markupEnabled || draft.allowIndividualMarkups,
    allowIndividualDiscounts: draft.discountEnabled || draft.allowIndividualDiscounts,
  };
}

/** Reset lines, then apply uniform markup/discount using the draft's explicit type. */
export function applyCommercialPricingDraft(
  lines: LinePricingDistributionLine[],
  draft: CommercialPricingDraft,
): Map<number, LinePricingFields> {
  const result = new Map<number, LinePricingFields>();
  lines.forEach((line) => {
    result.set(line.id, buildDefaultLinePricingFields(line.originalPrice));
  });

  const markupType = normalizeAdjustmentType(draft.markupType);
  const discountType = normalizeAdjustmentType(draft.discountType);
  const markupValue = roundMoney(Math.max(0, Number(draft.markupValue) || 0));
  const discountValue = roundMoney(Math.max(0, Number(draft.discountValue) || 0));

  if (draft.markupEnabled && !draft.allowIndividualMarkups && markupValue > 0) {
    const markupFieldsByLineId = applyUniformMarkupToLines(lines, {
      markupType,
      markupValue,
    });
    markupFieldsByLineId.forEach((fields, id) => {
      const current = result.get(id);
      if (current == null) return;
      result.set(id, {
        ...current,
        markupType: fields.markupType,
        markupValue: fields.markupValue,
        adjustedPrice: fields.adjustedPrice,
        finalPrice: fields.finalPrice,
      });
    });
  }

  if (draft.discountEnabled && !draft.allowIndividualDiscounts && discountValue > 0) {
    const linesWithMarkup = lines.map((line) => {
      const current = result.get(line.id);
      return {
        id: line.id,
        originalPrice: line.originalPrice,
        markupType: current?.markupType,
        markupValue: current?.markupValue,
      };
    });
    const discountFieldsByLineId = applyUniformDiscountToLines(linesWithMarkup, {
      discountType,
      discountValue,
    });
    discountFieldsByLineId.forEach((fields, id) => {
      const current = result.get(id);
      if (current == null) return;
      result.set(id, {
        ...current,
        discountType: fields.discountType,
        discountValue: fields.discountValue,
        finalPrice: fields.finalPrice,
      });
    });
  }

  return result;
}

/**
 * After save, store the new final as net vs catalog so the next edit starts
 * from the accepted sale. Not used as the in-wizard line representation.
 */
export function persistNetVsCatalog(
  originalPrice: number,
  newFinalPrice: number,
): LinePricingFields {
  const original = roundMoney(originalPrice);
  const finalPrice = clampMoney(newFinalPrice);

  if (finalPrice >= original) {
    const markupValue = roundMoney(finalPrice - original);
    return {
      markupType: markupValue > 0 ? 'AMOUNT' : 'NONE',
      markupValue,
      adjustedPrice: finalPrice,
      discountType: 'NONE',
      discountValue: 0,
      finalPrice,
    };
  }

  const discountValue = roundMoney(original - finalPrice);
  return {
    markupType: 'NONE',
    markupValue: 0,
    adjustedPrice: original,
    discountType: discountValue > 0 ? 'AMOUNT' : 'NONE',
    discountValue,
    finalPrice,
  };
}

/**
 * Edit path: increment then discount on the accepted line totals (not catalog).
 * Keeps this layer's % / Bs so the wizard can show green increment and red discount.
 */
export function applyCommercialDeltaOnSavedLines(
  lines: Array<{ id: number; originalPrice: number; savedFinalPrice: number }>,
  draft: CommercialPricingDraft,
): Map<number, LinePricingFields> {
  const working = lines.map((line) => ({
    id: line.id,
    originalPrice: line.savedFinalPrice,
  }));
  return applyCommercialPricingDraft(working, draft);
}

export function projectUniformAdjustedPrices(
  lines: Array<{ originalPrice: number }>,
  settings: { markupType: 'PERCENTAGE' | 'AMOUNT'; markupValue: number },
): Array<{ adjustedPrice: number }> {
  const distributionLines = lines.map((line, index) => ({
    id: index,
    originalPrice: line.originalPrice,
  }));
  const markupFieldsByLineId = applyUniformMarkupToLines(distributionLines, settings);

  return lines.map((line, index) => ({
    adjustedPrice: markupFieldsByLineId.get(index)?.adjustedPrice ?? roundMoney(line.originalPrice),
  }));
}
